// Orders Service - API integration for fetching user orders/service requests
import { authService } from './authService';
import { subServicesData } from '../data/servicesData';
import { servicePlans } from '../data/servicePlans';

// API Response Types
export interface ServiceRequest {
  id: number;
  serviceRequestUuid: string;
  customerId: string;
  serviceId: number;
  groupBookingId?: string;
  slotStart: string;
  slotEnd?: string;
  requestStatus: 'AWAITING_PAYMENT' | 'PENDING_PROVIDER' | 'ASSIGNED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  paymentMode: 'PREPAID' | 'POSTPAID';
  paymentStatus: 'PAID' | 'PENDING' | 'NOT_REQUIRED' | 'FAILED' | 'COMPLETED';
  priceEstimate: number;
  baseAmount: number;
  gstAmount: number;
  gstPercentage: number;
  convenienceFee: number;
  totalAmount: number;
  cancellationFee?: number;
  refundAmount?: number;
  cancelledAt?: string;
  cancellationReason?: string;
  assignedProviderId?: string;
  maxProviders?: number;
  assignedProviders?: AssignedProviderAssignment[];
  addressId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceAddress {
  id: number;
  addressUuid?: string;
  addressType: string;
  label: string;
  personName?: string;
  line1?: string;
  line2?: string;
  addressLine1?: string;
  addressLine2?: string;
  cityId?: number;
  stateId?: number;
  cityName?: string;
  stateName?: string;
  city: string;
  state: string;
  landmark?: string;
  postalCode?: string;
  pinCode?: string;
  latitude?: number;
  longitude?: number;
  createdAt?: string;
  updatedAt?: string;
  default?: boolean;
}

export interface AssignedProvider {
  id: number;
  providerUuid: string;
  userId: string;
  latitude: number;
  longitude: number;
  isAvailable: boolean;
  rating: number;
  experienceYears: number;
  status: 'ACTIVE' | 'INACTIVE';
  lastSeen: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: number;
}

export interface AssignedProviderAssignment {
  id: number;
  providerId: number;
  providerUuid: string;
  assignmentStatus: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';
  assignedAt: string;
  acceptedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  providerNotes?: string;
  userId: string;
  isAvailable: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  firstName: string;
  lastName: string;
  email: string;
  phone: number;
  providerName: string;
  providerEmail: string;
  providerPhone: string;
}

export interface BookingDetail {
  userUuid: string;
  serviceRequest: ServiceRequest;
  serviceAddress: ServiceAddress;
  assignedProvider: AssignedProvider;
}

export interface OrdersApiResponse {
  booking_details: BookingDetail[];
  total_count: number;
}

// Transformed interface for Orders component
export interface BookingOrder {
  id: string;
  orderId: string;
  serviceRequestId: string;
  serviceName: string;
  planName?: string;
  bookingDate: string;
  serviceDate: string;
  serviceTime: string;
  duration?: string;
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  paymentStatus: 'paid' | 'pending' | 'failed' | 'not_required' | 'completed';
  paymentMode: 'PREPAID' | 'POSTPAID';
  totalAmount: number;
  baseAmount: number;
  gstAmount: number;
  gstPercentage: number;
  convenienceFee: number;
  cancellationFee?: number;
  refundAmount?: number;
  cancelledAt?: string;
  cancellationReason?: string;
  groupBookingId?: string;
  address: {
    id?: string;
    name?: string;
    label?: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    landmark?: string;
    latitude?: number;
    longitude?: number;
    type?: string;
  };
  serviceImage?: string;
  createdAt: string;
  updatedAt: string;
  paymentDueDate?: string;
  priestDetails?: {
    name: string;
    phone: string;
    experience: string;
    rating: number;
  };
  assignedPriests?: {
    name: string;
    phone: string;
    experience: string;
    rating: number;
    status: string;
    assignedAt: string;
    acceptedAt?: string;
  }[];
  maxProviders?: number;
}

class OrdersService {
  private api = authService.getApiInstance();

  // Transform API response to Orders component format
  private transformBookingToOrder(booking: BookingDetail): BookingOrder {
    const { serviceRequest, serviceAddress, assignedProvider } = booking;

    // Debug pricing information
    console.log('🔍 Order pricing debug:', {
      orderId: serviceRequest.id,
      priceEstimate: serviceRequest.priceEstimate,
      totalAmount: serviceRequest.totalAmount,
      baseAmount: serviceRequest.baseAmount,
      gstAmount: serviceRequest.gstAmount,
      convenienceFee: serviceRequest.convenienceFee
    });
    
    // Map API status to component status
    const mapStatus = (status: string): 'confirmed' | 'pending' | 'completed' | 'cancelled' => {
      switch (status) {
        case 'AWAITING_PAYMENT': return 'pending';
        case 'PENDING_PROVIDER': return 'pending';
        case 'ASSIGNED': return 'confirmed';
        case 'CONFIRMED': return 'confirmed';
        case 'IN_PROGRESS': return 'confirmed';
        case 'COMPLETED': return 'completed';
        case 'CANCELLED': return 'cancelled';
        default: return 'pending';
      }
    };

    // Map payment status
    const mapPaymentStatus = (status: string): 'paid' | 'pending' | 'failed' | 'not_required' => {
      switch (status) {
        case 'PAID': return 'paid';
        case 'COMPLETED': return 'paid';
        case 'PENDING': return 'pending';
        case 'FAILED': return 'failed';
        case 'NOT_REQUIRED': return 'not_required';
        default: return 'pending';
      }
    };

    // Calculate service time and duration
    const startTime = new Date(serviceRequest.slotStart || serviceRequest.createdAt);
    
    // Get duration - prioritize service plans for realistic durations
    let displayDuration: string | undefined;
    let serviceDurationHours = 2; // Default duration in hours
    
    // Always try to get duration from service plans first for more accurate estimates
    const serviceName = this.getServiceNameById(serviceRequest.serviceId);
    const serviceKey = serviceName.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
    const plans = servicePlans[serviceKey];
    
    if (plans && plans.length > 0 && plans[0].duration) {
      // Use duration from service plans as it's more accurate
      displayDuration = plans[0].duration;
      
      // Parse duration to hours for time calculation
      const durationMatch = plans[0].duration.match(/(\d+(?:\.\d+)?)/);
      if (durationMatch) {
        serviceDurationHours = parseFloat(durationMatch[1]);
      } else if (plans[0].duration.includes('2-3')) {
        serviceDurationHours = 2.5; // Use middle value for ranges
      }
      
      console.log(`📅 Using service plan duration for ${serviceName}: ${displayDuration} (${serviceDurationHours}h)`);
    } else {
      // Calculate from API if available and reasonable
      const endTime = new Date(serviceRequest.slotEnd || serviceRequest.updatedAt || serviceRequest.createdAt);
      const durationMs = endTime.getTime() - startTime.getTime();
      const durationHours = Math.round(durationMs / (1000 * 60 * 60) * 10) / 10;
      
      if (durationHours >= 1) {
        // Only use calculated duration if it's reasonable (1+ hours)
        displayDuration = `${durationHours} hours`;
        serviceDurationHours = durationHours;
        console.log(`⏱️ Using calculated duration: ${displayDuration}`);
      } else {
        // Fallback for unknown services or small durations
        displayDuration = '2-3 hours';
        serviceDurationHours = 2.5;
        console.log(`🔄 Using fallback duration for ${serviceName}: ${displayDuration}`);
      }
    }
    
    // Calculate proper end time based on service duration
    const calculatedEndTime = new Date(startTime.getTime() + (serviceDurationHours * 60 * 60 * 1000));

    // Format time in Indian timezone with calculated end time
    const serviceTime = `${startTime.toLocaleTimeString('en-IN', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'Asia/Kolkata'
    })} - ${calculatedEndTime.toLocaleTimeString('en-IN', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'Asia/Kolkata'
    })}`;

    return {
      id: serviceRequest.serviceRequestUuid,
      orderId: `SRQ-${serviceRequest.id.toString().padStart(6, '0')}`,
      serviceRequestId: serviceRequest.serviceRequestUuid,
      serviceName: this.getServiceNameById(serviceRequest.serviceId),
      planName: undefined, // Not available in current API
      bookingDate: serviceRequest.createdAt,
      serviceDate: startTime.toISOString().split('T')[0],
      serviceTime: serviceTime,
      duration: displayDuration,
      status: mapStatus(serviceRequest.requestStatus),
      paymentStatus: mapPaymentStatus(serviceRequest.paymentStatus),
      paymentMode: serviceRequest.paymentMode,
      totalAmount: serviceRequest.totalAmount || 
                   (serviceRequest.baseAmount + serviceRequest.gstAmount + serviceRequest.convenienceFee) || 
                   serviceRequest.priceEstimate,
      baseAmount: serviceRequest.baseAmount || 0,
      gstAmount: serviceRequest.gstAmount || 0,
      gstPercentage: serviceRequest.gstPercentage || 0,
      convenienceFee: serviceRequest.convenienceFee || 0,
      cancellationFee: serviceRequest.cancellationFee,
      refundAmount: serviceRequest.refundAmount,
      cancelledAt: serviceRequest.cancelledAt,
      cancellationReason: serviceRequest.cancellationReason,
      address: serviceAddress && serviceAddress.id ? {
        id: serviceAddress?.id?.toString() || `addr_${serviceRequest.id}`,
        name: serviceAddress?.personName || serviceAddress?.label || 'Service Address',
        label: serviceAddress?.label || 'Service Address',
        line1: serviceAddress?.addressLine1 || serviceAddress?.line1 || '',
        line2: serviceAddress?.addressLine2 || serviceAddress?.line2 || '',
        city: serviceAddress?.city || 'Unknown City',
        state: serviceAddress?.state || 'Unknown State',
        postalCode: serviceAddress?.pinCode || serviceAddress?.postalCode || '',
        landmark: serviceAddress?.landmark,
        latitude: serviceAddress?.latitude,
        longitude: serviceAddress?.longitude,
        type: serviceAddress?.addressType || 'home',
      } : {
        // Antaryatra site location when addressId is null
        id: 'antaryatra-varanasi',
        name: 'Antaryatra Temple Site',
        label: 'Antaryatra Temple Site',
        line1: 'Sacred Temple Complex',
        line2: '',
        city: 'Varanasi',
        state: 'Uttar Pradesh',
        postalCode: '221001',
        landmark: 'Antaryatra Sacred Site',
        type: 'temple',
      },
      serviceImage: this.getServiceImageById(serviceRequest.serviceId),
      createdAt: serviceRequest.createdAt,
      updatedAt: serviceRequest.updatedAt,
      paymentDueDate: serviceRequest.paymentMode === 'POSTPAID' && serviceRequest.paymentStatus === 'PENDING' 
        ? serviceRequest.slotStart 
        : undefined,
      priestDetails: assignedProvider ? {
        name: `${assignedProvider.firstName} ${assignedProvider.lastName}`,
        phone: assignedProvider.phone.toString(),
        experience: `${assignedProvider.experienceYears} years`,
        rating: assignedProvider.rating,
      } : undefined,
      assignedPriests: serviceRequest.assignedProviders && serviceRequest.assignedProviders.length > 0 ? 
        serviceRequest.assignedProviders.map((assignment, index) => ({
          name: `${assignment.firstName} ${assignment.lastName}`,
          phone: assignment.providerPhone || assignment.phone.toString(),
          experience: 'Experienced', // Note: experience data not available in API yet
          rating: 4.5, // Note: rating data not available in API yet
          status: assignment.assignmentStatus,
          assignedAt: assignment.assignedAt,
          acceptedAt: assignment.acceptedAt
        })) : undefined,
      maxProviders: serviceRequest.maxProviders,
      groupBookingId: serviceRequest.groupBookingId,
    };
  }

  // Map service ID to service name
  private getServiceNameById(serviceId: number): string {
    // Get all services from all categories
    const allServices = Object.values(subServicesData).flat();
    const service = allServices.find(s => s.id === serviceId);
    return service?.name || `Service ${serviceId}`;
  }

  // Map service ID to image
  private getServiceImageById(serviceId: number): string {
    const serviceName = this.getServiceNameById(serviceId);
    return this.getServiceImage(serviceName);
  }

  // Map service name to image (kept for backward compatibility)
  private getServiceImage(serviceType: string): string {
    const imageMap: Record<string, string> = {
      'Maha Mrityunjaya Pujan': '/images/maha_mritunjay.jpg',
      'Rudrabhishekh': '/images/rudrabhishek.jpg',
      'Ganesha Pujan': '/images/puja_service.jpg',
      'Hanuman Chalisa Path': '/images/puja_service.jpg',
      'Vishnu Sahasranama': '/images/puja_service.jpg',
      'Maha Rudrai Anushthaan': '/images/puja_service.jpg',
    };
    
    return imageMap[serviceType] || '/images/puja_service.jpg';
  }

  // Fetch orders for a user
  async getUserOrders(userUuid: string): Promise<BookingOrder[]> {
    try {
      const response = await this.api.get<OrdersApiResponse>(`/api/user/${userUuid}`);
      
      return response.data.booking_details.map(booking => this.transformBookingToOrder(booking));
    } catch (error) {
      console.error('Error fetching user orders:', error);
      throw new Error('Failed to fetch orders. Please try again later.');
    }
  }

  // Get order by ID
  async getOrderById(orderUuid: string): Promise<BookingOrder | null> {
    try {
      const userUuid = authService.getUserUuid();
      if (!userUuid) {
        throw new Error('User not authenticated');
      }

      const orders = await this.getUserOrders(userUuid);
      return orders.find(order => order.id === orderUuid) || null;
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      throw error;
    }
  }

  // Calculate refund for service cancellation
  async calculateRefund(serviceRequestUuid: string, cancellationReason: string): Promise<{ 
    success: boolean; 
    message: string; 
    data?: {
      serviceRequestUuid: string;
      totalAmount: number;
      cancellationFee: number;
      refundAmount: number;
      refundStatus: string;
      reason: string;
      scheduledDateTime: string;
      cancellationDateTime: string;
      hoursBeforeService: number;
      hoursAfterBooking: number;
      appliedPolicy: {
        policyName: string;
        description: string;
        feePercentage: number;
        fixedFee: number;
        minHoursAfterBooking: number;
        maxHoursAfterBooking: number;
        policyType: string;
      };
      refundEligible: boolean;
    }
  }> {
    try {
      const response = await this.api.post('/api/cancellation/calculate-refund', {
        serviceRequestUuid,
        cancellationReason
      });
      
      return {
        success: response.data.status === 'SUCCESS',
        message: response.data.message || 'Refund calculation completed',
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error calculating refund:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to calculate refund. Please try again.';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Reschedule service request
  async rescheduleServiceRequest(
    serviceRequestUuid: string, 
    newSlotStart: string, 
    newSlotEnd: string, 
    reason: string = 'Customer requested different time'
  ): Promise<{ 
    success: boolean; 
    message: string; 
    data?: {
      currentStatus: string;
      newSlotStart: string;
      previousSlotStart: string;
      newSlotEnd: string;
      serviceRequestUuid: string;
      updatedAt: string;
      previousSlotEnd: string;
      status: string;
    }
  }> {
    try {
      const response = await this.api.put(`/api/booking/${serviceRequestUuid}/reschedule`, {
        newSlotStart,
        newSlotEnd,
        reason
      });
      
      return {
        success: response.data.status === 'SUCCESS',
        message: response.data.message || 'Service rescheduled successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error rescheduling service request:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to reschedule service request. Please try again.';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Cancel individual service request
  async cancelServiceRequest(serviceRequestUuid: string): Promise<{ success: boolean; message: string; refundInfo?: any }> {
    try {
      const response = await this.api.put(`/api/booking/${serviceRequestUuid}/cancel`);
      
      return {
        success: response.data.status === 'SUCCESS',
        message: response.data.message || 'Service request cancelled successfully',
        refundInfo: response.data.refundInfo
      };
    } catch (error: any) {
      console.error('Error cancelling service request:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to cancel service request. Please try again.';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Cancel entire group booking (cart booking)
  async cancelGroupBooking(groupBookingId: string): Promise<{ success: boolean; message: string; refundInfo?: any }> {
    try {
      const response = await this.api.put(`/api/booking/cart/${groupBookingId}/cancel`);
      
      return {
        success: response.data.status === 'SUCCESS',
        message: response.data.message || 'Group booking cancelled successfully',
        refundInfo: response.data.refundInfo
      };
    } catch (error: any) {
      console.error('Error cancelling group booking:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to cancel group booking. Please try again.';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }
}

export const ordersService = new OrdersService();